import { useEffect, useRef, useState } from "react";
import { PLANS, ACADEMY_ADDON } from "../lib/plan";
import { Brand } from "./Brand";

// ---- scroll-reveal wrapper ----
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } }, { threshold: 0.14 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`lp-reveal ${vis ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

// ---- count-up number ----
function CountUp({ to, prefix = "", suffix = "", dur = 1500 }: { to: number; prefix?: string; suffix?: string; dur?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [n, setN] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let raf = 0;
        const t0 = performance.now();
        const tick = (t: number) => { const p = Math.min(1, (t - t0) / dur); setN(Math.round(to * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
        raf = requestAnimationFrame(tick);
        io.disconnect();
        return () => cancelAnimationFrame(raf);
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, dur]);
  return <span ref={ref}>{prefix}{n.toLocaleString()}{suffix}</span>;
}

const FEATURES: [string, string, string][] = [
  ["📍", "Address → deal file", "Paste any address. Beacon geocodes it and pulls the tract, Section 8 rent ceiling, demographics, and flood zone automatically."],
  ["🏛️", "Real HUD / Section 8 math", "SAFMR ceilings, payment standards, HAP, utility allowances — the actual subsidy math, not a generic rent estimate."],
  ["🛡️", "Four compliance gates", "Physical (NSPIRE), rent reasonableness, PHA & eligibility, legal/title. Beacon stops a bad deal before you fall in love with it."],
  ["🎗️", "Eligibility & tax credits", "QCT · DDA · Opportunity Zone · USDA rural — including the 30% LIHTC basis boost most retail tools never touch."],
  ["🔍", "Foolproof due diligence", "A 78-point inspection matrix — every building system, cross-referenced to code, plus the Florida checks (4-point, wind-mit, WDO). Nothing left to chance."],
  ["⚖️", "Data-backed negotiation", "Every defect becomes documented leverage. Your revised offer is asking-minus-repairs, capped at the price that still makes you a profit — justified and safe."],
  ["✉️", "AI offer letters", "Claude turns your findings into a firm, professional letter to the seller or bank — then Beacon sends it, branded, from your own domain."],
  ["👥", "Your team, in your format", "Invite contractors, inspectors & estimators to their own role-limited seats. Their numbers flow straight into your underwriting."],
  ["🗂️", "A CRM you own", "A native pipeline board & contact book — your source of truth. Sync one-way to GoHighLevel, and export everything, anytime."],
  ["🌀", "Florida insurance stress-test", "The make-or-break number. Beacon models a hard-market premium spike so it never surprises you at renewal."],
  ["✨", "AI deal analyst", "Claude reads every number and gate and hands you a plain-English verdict: the upside, the #1 risk, the next step."],
  ["📈", "30-yr equity & the snowball", "Watch equity build to the dollar, then model refinancing one property to fund the next until you hit your income goal."],
];

const EXPLAIN: [string, string][] = [
  [
    "What exactly is Beacon?",
    "Beacon turns a property address into a full investment decision. Paste an address and it pulls the real government data, runs the Section 8 subsidy math, checks every compliance rule, stress-tests the risks, and hands you a single score — <b>Strong Buy to Do Not Proceed</b> — plus a plain-English write-up. Think of it as the underwriting desk of a professional real-estate fund, working for you in seconds.",
  ],
  [
    "What is Section 8 investing — and why is it worth doing?",
    "Section 8 (the Housing Choice Voucher program) is the federal government paying most of the rent for approved housing. For an owner that means <b>rent that's backed by the U.S. Treasury</b> — remarkably stable, rarely late, and set by a published HUD schedule (the SAFMR). Done right it combines dependable cash flow with real social impact. The catch is the rules; Beacon handles the rules.",
  ],
  [
    "How is this different from a rent calculator or a spreadsheet?",
    "A spreadsheet asks you for the rent. Beacon <b>tells you the rent</b> — the actual HUD payment standard for that exact address — then it checks the four things a spreadsheet can't: is the property physically eligible, is the rent reasonable, is the program/PHA path clear, and is the title/zoning clean. It also finds tax-credit eligibility (QCT/DDA/Opportunity Zone) most tools never touch.",
  ],
  [
    "What are the “four compliance gates”?",
    "Four hard checks that stop a bad deal early: <b>A — Physical</b> (HUD's NSPIRE inspection standard), <b>B — Rent</b> (the ceiling and rent-reasonableness test), <b>C — Program</b> (PHA activity and eligibility), and <b>D — Legal</b> (title, liens, zoning, environmental). If any gate fails, Beacon holds the score and tells you exactly why — before you fall in love with the property.",
  ],
  [
    "Where does the data come from?",
    "The foundation is <b>free government data</b> — the U.S. Census (geocoding, demographics, income), HUD (rent ceilings), and FEMA (flood). You only pay for optional convenience feeds: mortgages &amp; liens, market-rent comps, and nationwide parcel records. Every number is cited to its source and the date it was pulled, so you always know what you're standing on.",
  ],
  [
    "How does the AI actually help me?",
    "Once the numbers are in, Claude reads the whole deal — the returns, the gates, the insurance stress-test — and writes you a verdict a human analyst would: the upside in one line, the single biggest risk, and the next step to take. It's the difference between a screen full of metrics and <b>knowing what to do</b>.",
  ],
  [
    "How do I go from one property to many?",
    "This is the part that builds wealth. Beacon projects 30 years of equity — from loan paydown, appreciation, and the value you <em>force</em> by raising rents — then models the <b>refinance snowball</b>: pull equity out of property #1 to fund #2, then #3. Set a monthly passive-income goal and Beacon finds the acquisition plan that gets you there.",
  ],
  [
    "Can Beacon actually help me negotiate the price?",
    "Yes — this is where it pays for itself. Beacon runs a <b>foolproof 78-point inspection</b> (every building system, the Florida checks, public records), and every defect with a repair cost becomes documented leverage. It then sets your revised offer at the <em>lower</em> of (asking − those costs) and the highest price that still hits your return target — so the number is <b>justified and guaranteed profitable</b>. Then Claude writes the firm, data-backed letter to the seller or bank, and Beacon sends it from your own domain.",
  ],
  [
    "Can my team and contractors use it too?",
    "Yes. Invite your general contractor, inspector, or estimator to their own <b>role-limited seat</b> — they see only the deals you assign and submit their numbers in your format, which flow straight into your underwriting. Beacon also has a <b>native CRM</b> — a pipeline board and contact book that are <em>your</em> source of truth. Sync one-way to GoHighLevel if you like, and export everything anytime. You own your data.",
  ],
  [
    "Do I need to be an expert? Is it safe?",
    "No. Every term is explained in plain English right where it appears, so you learn while you work. And Beacon is deliberately <b>conservative</b> — it stress-tests insurance spikes and refinance shortfalls and shows you the downside up front. It's decision-support, not a sales pitch: the honest read is the whole point.",
  ],
];

export function Landing({ onStart, onDemo }: { onStart: () => void; onDemo: () => void }) {
  return (
    <div className="lp tp-landing">
      <header className="lp-nav">
        <Brand className="lp-brand" />
        <nav className="lp-nav-links">
          <a href="#process">Process</a>
          <a href="#evidence">Evidence</a>
          <button className="lp-btn ghost" onClick={onStart}>Sign in</button>
          <button className="lp-btn solid" onClick={onDemo}>Open Taylor Park</button>
        </nav>
      </header>

      <section className="lp-hero tp-landing-hero">
        <div className="lp-orb" />
        <div className="lp-hero-inner">
          <div className="lp-hero-copy">
            <div className="lp-eyebrow lp-fade">Taylor Park · Living investment repository</div>
            <h1 className="lp-fade" style={{ animationDelay: "90ms" }}>From brownfield evidence to an <span className="lp-underline">investment decision.</span></h1>
            <p className="lp-fade" style={{ animationDelay: "180ms" }}>
              One controlled workspace for the aerial, current zoning, cleanup history, development pro forma,
              ten-year cash flow, risk, readiness and investor reporting for 15458 W Dixie Highway.
            </p>
            <div className="lp-cta-row lp-fade" style={{ animationDelay: "270ms" }}>
              <button className="lp-btn solid lg" onClick={onDemo}>Open command center →</button>
              <button className="lp-btn ghost lg" onClick={onStart}>Sign in</button>
            </div>
            <div className="lp-trust lp-fade" style={{ animationDelay: "360ms" }}>Beacon by APAS Build · Source-linked · Version controlled · Decision-support</div>
          </div>
          <div className="lp-hero-visual lp-fade" style={{ animationDelay: "300ms" }}><HeroCard /></div>
        </div>
      </section>

      <section className="lp-stats" id="evidence">
        {[
          ["22", "versioned evidence records"],
          ["21/100", "current analysis readiness"],
          ["10 years", "NOI and cash-flow model"],
          ["1 PDF", "complete living history package"],
        ].map(([value, label]) => <div className="lp-stat" key={label}><div className="lp-stat-num">{value}</div><div className="lp-stat-label">{label}</div></div>)}
      </section>

      <section id="process" className="lp-section">
        <div className="lp-kicker">The Taylor Park method</div>
        <h2>Establish. Underwrite. Decide.</h2>
        <p className="lp-lead">The system moves from red to green only as dated, source-linked evidence replaces assumptions.</p>
        <div className="lp-steps">
          {[
            ["1", "Establish the property", "Prove gross versus net land, zoning and future land use, environmental obligations, utilities and financeable site control."],
            ["2", "Underwrite the plan", "Test a survey-based concept against market rents, costs, phasing, permanent debt, NOI, DSCR, value and sensitivity."],
            ["3", "Decide with evidence", "When the critical record is green, generate and preserve an API-assisted investment-committee report with risks and next actions."],
          ].map(([n, title, detail], index) => (
            <div className="lp-step" key={n}>
              <div className="lp-step-n">{n}</div><h3>{title}</h3><p>{detail}</p>
              {index < 2 && <div className="lp-step-arrow">→</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="lp-final">
        <div className="lp-orb two" />
        <h2>Open the Taylor Park decision room.</h2>
        <p>Review what is known, see what is missing and move the property toward an analysis-ready package.</p>
        <div className="lp-cta-row center"><button className="lp-btn solid lg" onClick={onDemo}>Enter Beacon →</button></div>
      </section>

      <footer className="lp-footer">
        <Brand className="lp-brand small" compact />
        <div>Taylor Park property intelligence · 15458 W Dixie Highway</div>
        <div className="lp-foot-fine">Decision-support, not investment advice. Assumptions remain concepts until verified.</div>
      </footer>
    </div>
  );
}

function LegacyLanding({ onStart, onDemo }: { onStart: () => void; onDemo: () => void }) {
  const [annual, setAnnual] = useState(true);
  return (
    <div className="lp">
      {/* NAV */}
      <header className="lp-nav">
        <Brand className="lp-brand" />
        <nav className="lp-nav-links">
          <a href="#how">How it works</a>
          <a href="#different">Why Beacon</a>
          <a href="#depth">In depth</a>
          <a href="#pricing">Pricing</a>
          <button className="lp-btn ghost" onClick={onStart}>Sign in</button>
          <button className="lp-btn solid" onClick={onStart}>Get started</button>
        </nav>
      </header>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-orb" />
        <div className="lp-hero-inner">
          <div className="lp-hero-copy">
            <div className="lp-eyebrow lp-fade" style={{ animationDelay: "0ms" }}>Property intelligence · Built by APAS</div>
            <h1 className="lp-fade" style={{ animationDelay: "90ms" }}>Underwrite the address. <span className="lp-underline">Illuminate the decision.</span></h1>
            <p className="lp-fade" style={{ animationDelay: "180ms" }}>
              Beacon underwrites affordable-housing deals against real HUD math, turns your inspection findings into a
              data-backed offer, and compounds one property into many — with your team and your CRM built in.
              Paste an address. Win the deal. Build wealth.
            </p>
            <div className="lp-cta-row lp-fade" style={{ animationDelay: "270ms" }}>
              <button className="lp-btn solid lg" onClick={onStart}>Get started free →</button>
              <button className="lp-btn ghost lg" onClick={onDemo}>See it in action</button>
            </div>
            <div className="lp-trust lp-fade" style={{ animationDelay: "360ms" }}>No credit card · Free to start · Decision-support, not advice</div>
          </div>
          <div className="lp-hero-visual lp-fade" style={{ animationDelay: "300ms" }}>
            <HeroCard />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="lp-stats">
        {[
          { to: 7, suf: " feeds", label: "government data sources, wired" },
          { to: 4, suf: " gates", label: "compliance checks per deal" },
          { to: 78, suf: "-point", label: "due-diligence inspection matrix" },
          { to: 30, suf: "-yr", label: "wealth projection, per property" },
        ].map((s, i) => (
          <Reveal key={i} delay={i * 90} className="lp-stat">
            <div className="lp-stat-num"><CountUp to={s.to} />{s.suf}</div>
            <div className="lp-stat-label">{s.label}</div>
          </Reveal>
        ))}
      </section>

      {/* HOW IT WORKS 1-2-3 */}
      <section id="how" className="lp-section">
        <Reveal><div className="lp-kicker">How it works</div><h2>As simple as one, two, three.</h2></Reveal>
        <div className="lp-steps">
          {[
            ["1", "Paste an address", "From an auction, HUD Home Store, a tax-deed list, or an agent's export — one or a hundred at once."],
            ["2", "AI does the work", "Rent ceilings, demographics, flood, four compliance gates, and a plain-English verdict — automatically."],
            ["3", "Win it & scale", "Turn your inspection into a data-backed offer, send it from your domain, close — then refinance into your next property."],
          ].map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 120} className="lp-step">
              <div className="lp-step-n">{n}</div>
              <h3>{t}</h3><p>{d}</p>
              {i < 2 && <div className="lp-step-arrow">→</div>}
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp-section alt">
        <Reveal><div className="lp-kicker">The whole lifecycle, one platform</div><h2>From an address to a signed offer.</h2></Reveal>
        <div className="lp-features">
          {FEATURES.map(([icon, t, d], i) => (
            <Reveal key={t} delay={(i % 4) * 80} className="lp-feature">
              <div className="lp-feature-icon">{icon}</div>
              <h3>{t}</h3><p>{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section id="different" className="lp-section">
        <Reveal><div className="lp-kicker">Why Beacon</div><h2>Most calculators give you a cap rate.<br /><span className="lp-brass">Beacon gives you the decision.</span></h2></Reveal>
        <div className="lp-compare">
          <Reveal className="lp-compare-col them">
            <div className="lp-compare-h">Generic deal calculators</div>
            <ul>
              <li>Market rent guesses</li>
              <li>No compliance or eligibility</li>
              <li>No inspection → no leverage</li>
              <li>No help writing or sending the offer</li>
              <li>A number — then you're on your own</li>
              <li>Your data locked inside their tool</li>
            </ul>
          </Reveal>
          <Reveal delay={120} className="lp-compare-col us">
            <div className="lp-compare-h">Beacon</div>
            <ul>
              <li>Real HUD SAFMR ceilings &amp; subsidy math</li>
              <li>Four compliance gates + tax-credit eligibility</li>
              <li>A 78-point inspection that becomes price leverage</li>
              <li>A revised offer that's justified <em>and</em> profitable</li>
              <li>AI offer letters, sent from your own domain</li>
              <li>Your team, your CRM, your data — you own it</li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* VALUE / FOR WHOM */}
      <section className="lp-section alt">
        <Reveal><div className="lp-kicker">Built for how you invest</div><h2>Investor-grade. Enterprise-ready.</h2></Reveal>
        <div className="lp-audience">
          <Reveal className="lp-aud-card">
            <div className="lp-aud-tag">Individual</div>
            <h3>The solo investor</h3>
            <p>Analyze deals like a fund does — without a fund's team. Every property scored, every risk surfaced, every inspection finding turned into offer leverage, every dollar of future equity mapped. Your time is money; Beacon gives hours back per deal.</p>
          </Reveal>
          <Reveal delay={120} className="lp-aud-card dark">
            <div className="lp-aud-tag">Organization</div>
            <h3>The team &amp; the fund</h3>
            <p>Role-limited seats for your contractors and inspectors — they submit in your format. A native pipeline and contact book you own, one-way sync to GoHighLevel, and branded offer letters from your domain. Own your data. White-label it. Scale sourcing to hundreds of addresses at a click.</p>
          </Reveal>
        </div>
      </section>

      {/* AI + FUTURE */}
      <section id="future" className="lp-section">
        <div className="lp-future">
          <Reveal className="lp-future-copy">
            <div className="lp-kicker">Your future</div>
            <h2>AI-driven today.<br />Community-driven tomorrow.</h2>
            <p>Beacon already reads your deals and writes the analysis. What comes next is shaped by you — every subscriber's request feeds the roadmap. We ship, constantly.</p>
            <div className="lp-roadmap">
              {["Live auction & MLS feeds", "GoHighLevel two-way sync", "Automated follow-up campaigns", "Tenant & PHA screening", "Full LIHTC / bond modeling", "Deeper AI: portfolio strategy"].map((r) => (
                <span key={r} className="lp-chip">{r}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="lp-future-edu">
            <div className="lp-edu-card">
              <div className="lp-edu-icon">📖</div>
              <h3>Learn as you go</h3>
              <p>Every term explained in plain English, right where you need it — a built-in glossary and tooltips turn Beacon into the fastest way to <em>learn</em> Section 8 investing while you do it.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* IN DEPTH — collapsible explainer so anyone "gets it" */}
      <section id="depth" className="lp-section">
        <Reveal><div className="lp-kicker">In depth</div><h2>Everything you might be wondering.</h2>
          <p className="lp-lead">New to affordable-housing investing? Open any question. This is the whole thing, in plain English — no jargon, no gatekeeping.</p>
        </Reveal>
        <div className="lp-faq">
          {EXPLAIN.map(([q, a], i) => (
            <Reveal key={q} delay={(i % 2) * 80} className="lp-faq-reveal">
              <details className="lp-faq-item">
                <summary><span>{q}</span><i className="lp-faq-mark" /></summary>
                <div className="lp-faq-a" dangerouslySetInnerHTML={{ __html: a }} />
              </details>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lp-section alt">
        <Reveal>
          <div className="lp-kicker">Pricing</div>
          <h2>Start free. Level up as you grow.</h2>
          <p className="lp-lead">Analyze solo, bring in your crew, then raise capital — the plan grows with you. <b>One good deal pays for a decade of Beacon.</b></p>
          <div className="lp-bill">
            <button className={!annual ? "on" : ""} onClick={() => setAnnual(false)}>Monthly</button>
            <button className={annual ? "on" : ""} onClick={() => setAnnual(true)}>Annual <span className="lp-save">2 months free</span></button>
          </div>
        </Reveal>
        <div className="lp-pricing four">
          {PLANS.map((p, i) => {
            const featured = p.id === "firm";
            const price = p.price === 0 ? "$0" : annual ? "$" + (p.price * 10).toLocaleString() : "$" + p.price;
            const per = p.price === 0 ? "forever" : annual ? "/yr" : "/mo";
            return (
              <Reveal key={p.id} delay={i * 80} className={`lp-price ${featured ? "featured" : ""}`}>
                {featured ? <div className="lp-price-badge">Most popular</div> : null}
                <div className="lp-price-name">{p.name}</div>
                <div className="lp-price-who">{p.who}</div>
                <div className="lp-price-amt">{price}<span>{per}</span></div>
                {annual && p.price > 0 && <div className="lp-price-eq">${p.price}/mo billed annually</div>}
                <ul>{p.features.map((f) => <li key={f}>✓ {f}</li>)}</ul>
                <button className={`lp-btn ${featured ? "solid" : "ghost"} full`} onClick={onStart}>{p.price === 0 ? "Get started free" : `Start ${p.name}`}</button>
              </Reveal>
            );
          })}
        </div>
        <div className="lp-price-note">
          <b>Beacon Academy</b> (all courses + certificates) is <b>+${ACADEMY_ADDON}/user/mo</b> on Operator — and <b>included free</b> in Firm &amp; Syndicate.
          Paid data feeds (ATTOM, RentCast) &amp; heavy AI usage billed at cost. Syndication is a flat SaaS fee — never a cut of your raise.
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-final">
        <div className="lp-orb two" />
        <Reveal>
          <h2>Your next deal is one address away.</h2>
          <p>Join the investors turning Section 8 into generational wealth — with the intelligence to do it right.</p>
          <div className="lp-cta-row center">
            <button className="lp-btn solid lg" onClick={onStart}>Get started free →</button>
            <button className="lp-btn ghost lg light" onClick={onDemo}>See it in action</button>
          </div>
        </Reveal>
      </section>

      <footer className="lp-footer">
        <Brand className="lp-brand small" compact />
        <div>Section 8 / affordable-housing investment intelligence · Florida-first</div>
        <div className="lp-foot-fine">Decision-support, not investment advice. Illustrative figures until live data is connected.</div>
      </footer>
    </div>
  );
}

// ---- animated hero visual: a property "deal file" with a drawing score ring ----
function HeroCard() {
  return (
    <div className="lp-card">
      <div className="lp-card-top">
        <div>
          <div className="lp-card-eyebrow">DECISION FILE · NORTH MIAMI BEACH</div>
          <div className="lp-card-title">Taylor Park</div>
        </div>
        <svg className="lp-ring" width="72" height="72" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="50" fill="none" stroke="#EBE5D6" strokeWidth="10" />
          <circle className="lp-ring-fg tp-ring-red" cx="60" cy="60" r="50" fill="none" stroke="#B64D49" strokeWidth="10" strokeLinecap="round" strokeDasharray="66 314" transform="rotate(-90 60 60)" />
          <text x="60" y="58" textAnchor="middle" className="lp-ring-num">21</text>
          <text x="60" y="76" textAnchor="middle" className="lp-ring-lab">READY</text>
        </svg>
      </div>
      <div className="lp-card-kpis">
        {[["Zoning", "CF / ROS"], ["Gross parcel", "21.05 ac"], ["Concept NOI", "$12.48m"], ["Value / cost", "58.0%"]].map(([l, v]) => (
          <div key={l} className="lp-card-kpi"><div className="k">{l}</div><div className="v">{v}</div></div>
        ))}
      </div>
      <div className="tp-hero-aerial" role="img" aria-label="2025 aerial of Taylor Park" />
      <div className="lp-card-foot">● RED · Hold for survey, zoning, environmental, market and cost evidence.</div>
    </div>
  );
}
